import {AnyFunction, logger} from 'yougile-util'
import {
  Branch,
  Connections,
  DataObject,
  DataObjectId,
  DataObjectJSON,
  Diff,
  HubUtil,
  SandboxTree
} from 'yougile-model'
import {Backend} from '@common/backend'
import {globals} from '@extensions/globals-base'
import {invalidateTaskDataCache} from '@extensions/task-data-extractor'
import {Connector} from './connector'
import {Sync} from './sync'
import {SyncEvents} from './sync-events'

export class SyncCommitHandler {
  private static commitRequests: Map<
    DataObjectId,
    {id: DataObjectId; companyId: DataObjectId; from?: DataObjectId}
  > = new Map()

  private static delayedCommitRequestCount = 0

  static doInBatch<Fn extends AnyFunction>(fn: Fn): ReturnType<Fn> {
    this.startBatch()
    try {
      const res = fn() as ReturnType<Fn>
      this.stopBatch()
      return res
    } catch (e) {
      logger.log('Error in batch', e)
      this.stopBatch()
      throw e
    }
  }

  static async asyncDoInBatch<R>(fn: () => Promise<R>): Promise<R> {
    this.startBatch()
    try {
      const res = await fn()
      this.stopBatch()
      return res
    } catch (e) {
      logger.log('Error in batch', e)
      this.stopBatch()
      throw e
    }
  }

  private static startBatch() {
    this.delayedCommitRequestCount++
  }

  private static stopBatch() {
    this.delayedCommitRequestCount--
    if (this.delayedCommitRequestCount < 0) {
      this.delayedCommitRequestCount = 0
    }
    const objects = [...this.commitRequests.values()]
    this.commitRequests.clear()
    for (const o of objects) {
      const box = Sync.get(o)
      const obj = box?.get(o.id) ?? box.getConnectionFrom(o.from)
      if (obj) {
        try {
          this.onDataObjectCommit(obj, false)
        } catch (e) {
          logger.error(e)
        }
      } else {
        logger.error('Object is not found', o)
      }
    }
  }

  static onDataObjectCommit = (obj: DataObject, forced: boolean) => {
    if (!Sync.listSandboxes()) {
      logger.warn('Not initialized yet, postponed')
      setTimeout(() => SyncCommitHandler.onDataObjectCommit(obj, forced), 2000)
      return
    }
    if (SyncCommitHandler.delayedCommitRequestCount > 0 && !forced) {
      SyncCommitHandler.commitRequests.set(obj.id, {
        id: obj.id,
        companyId: obj.companyId,
        from: obj instanceof Connections ? obj.from : undefined
      })
      return
    }
    const sData = Sync.latestRemoteData.get(obj.companyId)
    if (!sData) {
      logger.warn('Can not commit now')
      return
    }
    invalidateTaskDataCache(obj.id)
    let locJSON: DataObjectJSON = HubUtil.withoutLazyPlaceholders(
      sData?.index[obj.id]
    )
    Sync.unapprovedChanges.forEach(branch => {
      if (Sync.processedBranchesIds.has(branch.id)) {
        return
      }
      if (branch.companyId === obj.companyId && branch.changes[obj.id]) {
        locJSON = branch.applyInPlace(obj.companyId, obj.id, locJSON)
      }
    })

    Sync.unprocessedChanges.forEach(branch => {
      if (branch.companyId === obj.companyId && branch.changes[obj.id]) {
        locJSON = branch.applyInPlace(obj.companyId, obj.id, locJSON)
      }
    })
    if (!locJSON) {
      locJSON = undefined
    }

    let changedConn: DataObjectId[]
    if (obj instanceof Connections) {
      const c = obj.getChanged()
      if (c !== 'all') {
        changedConn = c
      }
    }

    const newObjJson = HubUtil.withoutLazyPlaceholders(obj.toJSON())
    const diff = Diff.calculate(locJSON, newObjJson, changedConn)
    if (Diff.isEmpty(diff)) {
      logger.log('Skipping empty diff')
      return
    }

    setTimeout(() => {
      const upd = SyncEvents.updFromObjectAndDiff(
        SandboxTree.jsonToObject(obj.companyId, locJSON),
        diff,
        {
          fromRemote: false,
          companyId: obj.companyId
        }
      )
      SyncEvents.triggerUpdate(obj.companyId, [upd], false)
    }, 100)

    if (!obj.companyId) {
      logger.error('No company id specified', obj)
      return
    }
    const bs = new Branch(obj.companyId)
    bs.fromRevision = sData.revision
    bs.changes[obj.id] = diff
    if (forced) {
      const r = JSON.stringify(bs)
      if (r.length > 2000000) {
        Backend.report(
          'Large (forced) branch skipped',
          globals.currentUserId,
          r.length
        )
        return
      }
      Sync.localBranchesIds.add(bs.id)
      Sync.unapprovedChanges.set(bs.id, bs)
      Connector.commit(bs)
    } else {
      Sync.unprocessedChanges.set(bs.id, bs)
      Sync.commitDebouncer.process(bs) // see processLocalChanges
    }
    Sync.saveUnapproved()
  }
}
